import React, { useState } from 'react';
import Cognito from '../../utils/Cognito';
import { Updater, useImmer } from 'use-immer';
import { ICognitoResponse, ILoginInput, LoginStep } from '../types';
import styled from 'styled-components';
import { AxiosError } from 'axios';
import { useNavigate } from 'react-router-dom';
import { TOAST_ERROR_MESSAGE } from '../../utils/utils';
import { UnreachableCaseError } from 'ts-essentials';
import { goBackToLoginStep } from './utils';
import toastMessage from '../../../../utils/toastMessage';
import AuthFormPasswordField from './AuthFormPasswordField';
import { FieldError } from '../../../../components/Form';
import { theme } from '../../../../styles/theme';
import {
  AuthFieldContainer,
  AuthFieldLabel,
  AuthFormContainer,
  AuthInputField,
  AuthSubmitButton,
} from '../LoginPagesUI';

interface IProps {
  email: string;
  setLoginStep: (step: LoginStep) => void;
  setLoginInput: Updater<ILoginInput>;
  loginStep: LoginStep.ConfirmForgotPassword | LoginStep.ChangePassword;
}

interface IPasswordInput {
  value: string;
  hasError: boolean;
}
interface IPasswords {
  oldPassword: IPasswordInput;
  newPassword: IPasswordInput;
  confirmPassword: IPasswordInput;
}

const ConfirmPasswordContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 16px;
`;
const PasswordRules = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 2px;
  font-size: 10px;
  font-weight: 400;
  color: ${theme.colors.secondary800};
`;
const PasswordRulesList = styled.ul`
  margin: 0;
  padding-left: 16px;
`;

// text need to display for all special characters
const SPECIAL_CHARACTERS =
  '^ $ * . [ ] { } ( ) ? - “ ! @\n' +
  '        # % & /\\ , > < ‘ : ; | _ ~ ` + =';

// regex for cognito password validation
// https://stackoverflow.com/questions/58767980/aws-cognito-password-regex-specific-to-aws-cognito
// https://regexr.com/79p07
const PASSWORD_REGEX_PATTERN = new RegExp(
  '^(?!\\s+)(?!.*\\s+$)(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*[$^*.[\\]{}()?"!@#%&/\\\\,><\':;|_~`=+\\- ])' +
    '[A-Za-z0-9$^*.[\\]{}()?"!@#%&/\\\\,><\':;|_~`=+\\- ]{8,256}$',
);
const initialPasswordState: IPasswordInput = {
  value: '',
  hasError: false,
};

const ChangePasswordForm = ({
  setLoginInput,
  loginStep,
  email,
  setLoginStep,
}: IProps) => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [passwords, setPasswords] = useImmer<IPasswords>({
    oldPassword: initialPasswordState,
    newPassword: initialPasswordState,
    confirmPassword: initialPasswordState,
  });

  const onSubmitPasswordForgotVerifyHandler = () => {
    Cognito.confirmForgotPassword(
      email,
      passwords.oldPassword.value,
      passwords.newPassword.value,
    )
      .then(() => {
        toastMessage({
          message: 'Password changed successfully',
          type: 'info',
        });
        setLoginStep(LoginStep.NotLoggedIn);
        setLoginInput((draft) => {
          draft.email = '';
          draft.password = '';
        });
      })
      .catch((error: AxiosError<ICognitoResponse>) => {
        setIsSubmitting(false);
        const status = error.response?.status;
        switch (status) {
          case 400:
          case 401:
            setPasswords((draft) => {
              draft.oldPassword.hasError = true;
            });
            return;
          default:
            console.error('Change Password Error: ', error.response?.data);
            toastMessage({ message: TOAST_ERROR_MESSAGE, type: 'error' });
            goBackToLoginStep({ setLoginStep, setLoginInput });
        }
      });
  };
  const onSubmitPasswordChangeHandler = () => {
    Cognito.changePassword(
      email,
      passwords.oldPassword.value,
      passwords.newPassword.value,
    )
      .then(() => {
        // redirect user to homepage after successful password change
        navigate('/');
      })
      .catch((err: AxiosError<ICognitoResponse>) => {
        setIsSubmitting(false);
        const challenge = err.response?.data?.challenge;
        switch (challenge) {
          // in current design, MFA_SETUP would be the only challenge for change password
          case 'MFA_SETUP':
            setLoginStep(LoginStep.MfaSetup);
            return;
          default:
            const status = err.response?.status;
            if (status === 401) {
              // current password is incorrect
              setPasswords((draft) => {
                draft.oldPassword.hasError = true;
              });
            } else if (status === 400) {
              // password does not meet the requirements
              setPasswords((draft) => {
                draft.newPassword.hasError = true;
              });
            } else {
              // unknown error
              toastMessage({ message: TOAST_ERROR_MESSAGE, type: 'error' });
              console.error('Change Password Error: ', err.response?.data);
              goBackToLoginStep({ setLoginStep, setLoginInput });
            }
        }
      });
  };

  const onSubmitHandler = (e: React.MouseEvent) => {
    e.preventDefault();
    // terminate if any of the password fields has an error, just in case
    if (passwords.newPassword.value !== passwords.confirmPassword.value) {
      setPasswords((draft) => {
        draft.confirmPassword.hasError = true;
      });
      return;
    } else if (!PASSWORD_REGEX_PATTERN.test(passwords.newPassword.value)) {
      setPasswords((draft) => {
        draft.newPassword.hasError = true;
      });
      return;
    }
    setIsSubmitting(true);

    if (loginStep === LoginStep.ChangePassword) {
      onSubmitPasswordChangeHandler();
    } else if (loginStep === LoginStep.ConfirmForgotPassword) {
      onSubmitPasswordForgotVerifyHandler();
    } else {
      throw new UnreachableCaseError(loginStep);
    }
  };

  const onSetPassword = (password: string, passwordKey: keyof IPasswords) => {
    setPasswords((draft) => {
      draft[passwordKey] = { value: password, hasError: false };
    });
  };

  /**
   * onBlur event for new password field
   * check if the password meets the requirements
   * if yes, also check if the confirmed password field matches the new password
   */
  const onNewPasswordBlur = () => {
    const isValidPassword = PASSWORD_REGEX_PATTERN.test(
      passwords.newPassword.value,
    );
    if (!isValidPassword) {
      setPasswords((draft) => {
        draft.newPassword.hasError = true;
      });
      return;
    }

    if (passwords.newPassword.value === passwords.confirmPassword.value) {
      setPasswords((draft) => {
        draft.confirmPassword.hasError = false;
      });
    }
  };

  /**
   * onBlur event for confirm password field
   * check if the confirm password matches the new password
   */
  const onConfirmPasswordBlur = () => {
    if (passwords.newPassword.value !== passwords.confirmPassword.value) {
      setPasswords((draft) => {
        draft.confirmPassword.hasError = true;
      });
    }
  };

  return (
    <AuthFormContainer>
      {loginStep === LoginStep.ChangePassword && (
        <AuthFieldContainer>
          <AuthFieldLabel>Old password</AuthFieldLabel>
          <AuthFormPasswordField
            placeholder={'Enter old password'}
            value={passwords.oldPassword.value}
            onChange={(value) => onSetPassword(value, 'oldPassword')}
            autoComplete={'off'}
            hasError={passwords.oldPassword.hasError}
          />
          {passwords.oldPassword.hasError && (
            <FieldError type="custom" customError={'Password is incorrect'} />
          )}
        </AuthFieldContainer>
      )}
      {loginStep === LoginStep.ConfirmForgotPassword && (
        <AuthFieldContainer>
          <AuthFieldLabel>Code</AuthFieldLabel>
          <AuthInputField
            type="text"
            placeholder={'Password reset code from email'}
            value={passwords.oldPassword.value}
            onChange={(value) => onSetPassword(value, 'oldPassword')}
            autoComplete={'off'}
            hasError={passwords.oldPassword.hasError}
          />
          {passwords.oldPassword.hasError && (
            <FieldError
              type="custom"
              customError={'Reset code is not correct'}
            />
          )}
        </AuthFieldContainer>
      )}

      <div>
        {/* To remove the warning from Chrome about missing username/email. */}
        <input type="hidden" value={email} readOnly autoComplete="username" />
        <AuthFieldContainer>
          <AuthFieldLabel>New password</AuthFieldLabel>
          <AuthFormPasswordField
            placeholder={'Enter new password'}
            value={passwords.newPassword.value}
            onChange={(value) => onSetPassword(value, 'newPassword')}
            autoComplete={'new-password'}
            onBlur={onNewPasswordBlur}
            hasError={passwords.newPassword.hasError}
          />
          {passwords.newPassword.hasError && (
            <FieldError
              type="custom"
              customError={'New password does not meet the requirements'}
            />
          )}
        </AuthFieldContainer>
      </div>
      <ConfirmPasswordContainer>
        <AuthFieldContainer>
          <AuthFieldLabel>Confirm password</AuthFieldLabel>
          <AuthFormPasswordField
            placeholder={'Confirm Password'}
            value={passwords.confirmPassword.value}
            onChange={(value) => onSetPassword(value, 'confirmPassword')}
            autoComplete={'new-password'}
            onBlur={onConfirmPasswordBlur}
            hasError={passwords.confirmPassword.hasError}
          />
          {passwords.confirmPassword.hasError && (
            <FieldError type="custom" customError={'Password does not match'} />
          )}
        </AuthFieldContainer>
        <PasswordRules>
          Password must:
          <PasswordRulesList>
            <li>Contain at least 8 characters</li>
            <li>Contain both lower and uppercase letters</li>
            <li>Contain at least 1 number</li>
            <li>Contain at least 1 special character: {SPECIAL_CHARACTERS}</li>
          </PasswordRulesList>
        </PasswordRules>
      </ConfirmPasswordContainer>

      <AuthSubmitButton
        type="submit"
        size="default"
        onClick={(e) => onSubmitHandler(e)}
        loading={isSubmitting}
        trackContext="login"
        trackAction="change password - submit"
      >
        Submit
      </AuthSubmitButton>
    </AuthFormContainer>
  );
};

export default ChangePasswordForm;
